import { useState, useEffect } from "react";
import { api } from "@/App";
import AdminLayout from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Settings, Loader2 } from "lucide-react";
import { toast } from "sonner";

const AdminSettings = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    points_per_dollar: "",
    points_per_mile: "",
    voucher_expiry_days: "",
    vehicle_multipliers: {}
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await api.get("/admin/points-config");
      setConfig(response.data);
      setFormData({
        points_per_dollar: response.data.points_per_dollar.toString(),
        points_per_mile: response.data.points_per_mile.toString(),
        voucher_expiry_days: response.data.voucher_expiry_days.toString(),
        vehicle_multipliers: response.data.vehicle_multipliers || {}
      });
    } catch (error) {
      toast.error("Failed to load configuration");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/admin/points-config", null, {
        params: {
          points_per_dollar: parseFloat(formData.points_per_dollar),
          points_per_mile: parseFloat(formData.points_per_mile),
          voucher_expiry_days: parseInt(formData.voucher_expiry_days)
        }
      });
      toast.success("Configuration saved");
      fetchConfig();
    } catch (error) {
      toast.error("Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  const updateMultiplier = (vehicle, value) => {
    setFormData(prev => ({
      ...prev,
      vehicle_multipliers: {
        ...prev.vehicle_multipliers,
        [vehicle]: parseFloat(value) || 1
      }
    }));
  };

  if (loading) {
    return (
      <AdminLayout title="Points Configuration">
        <div className="space-y-4">
          {[1,2].map(i => (
            <div key={i} className="h-48 rounded-xl animate-shimmer" />
          ))}
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Points Configuration">
      <div className="space-y-6 animate-fade-in max-w-2xl">
        {/* Base Points */}
        <Card className="bg-[#131316] border-[#2A2A2F]">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Settings size={20} className="text-[#D4AF37]" />
              Points Calculation
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#A0A0A0]">Points per Dollar</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.points_per_dollar}
                  onChange={(e) => setFormData({ ...formData, points_per_dollar: e.target.value })}
                  className="bg-[#1A1A1E] border-[#2A2A2F] text-white focus:border-[#D4AF37]"
                  data-testid="points-per-dollar-input"
                />
                <p className="text-[#6B6B6B] text-xs">Miles earned per dollar spent</p>
              </div>
              
              <div className="space-y-2">
                <Label className="text-[#A0A0A0]">Points per Mile</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.points_per_mile}
                  onChange={(e) => setFormData({ ...formData, points_per_mile: e.target.value })}
                  className="bg-[#1A1A1E] border-[#2A2A2F] text-white focus:border-[#D4AF37]"
                  data-testid="points-per-mile-input"
                />
                <p className="text-[#6B6B6B] text-xs">Miles earned per mile traveled</p>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-[#A0A0A0]">Voucher Expiry (Days)</Label>
              <Input
                type="number"
                value={formData.voucher_expiry_days}
                onChange={(e) => setFormData({ ...formData, voucher_expiry_days: e.target.value })}
                className="bg-[#1A1A1E] border-[#2A2A2F] text-white focus:border-[#D4AF37] max-w-xs"
                data-testid="voucher-expiry-input"
              />
              <p className="text-[#6B6B6B] text-xs">Number of days before vouchers expire</p>
            </div>
          </CardContent>
        </Card>

        {/* Vehicle Multipliers */}
        <Card className="bg-[#131316] border-[#2A2A2F]">
          <CardHeader>
            <CardTitle className="text-white">Vehicle Type Multipliers</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-[#A0A0A0] text-sm mb-4">
              Points are multiplied by these values based on vehicle type
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(formData.vehicle_multipliers).map(([vehicle, multiplier]) => (
                <div key={vehicle} className="space-y-2">
                  <Label className="text-[#A0A0A0] capitalize">{vehicle}</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={multiplier}
                    onChange={(e) => updateMultiplier(vehicle, e.target.value)}
                    className="bg-[#1A1A1E] border-[#2A2A2F] text-white focus:border-[#D4AF37]"
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-gradient-to-r from-[#D4AF37] to-[#B8942D] text-[#0A0A0B] font-semibold h-12 px-8"
          data-testid="save-config-btn"
        >
          {saving ? (
            <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving...</>
          ) : (
            'Save Configuration'
          )}
        </Button>

        {/* Formula Preview */}
        <Card className="bg-[#0A0A0B] border-[#2A2A2F]">
          <CardContent className="p-4">
            <p className="text-[#D4AF37] text-sm font-medium mb-2">Points Formula:</p>
            <code className="text-[#A0A0A0] text-sm">
              Points = (Fare × {formData.points_per_dollar}) + (Distance × {formData.points_per_mile}) × Vehicle Multiplier × Tier Multiplier
            </code>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
